import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Bug, MessageCircle, MoreHorizontal, PanelRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MOBILE_ACTION_BAR_HEIGHT_VAR } from "@/components/ui/mobile-action-bar";

/**
 * Below the `lg` breakpoint the Chat pill, the claim-detail Activity button and
 * the Bugs & Features button collapse into one round button with a menu. Each
 * control registers its item with useMobileFabItem and drops its own trigger
 * while the FAB is showing. At `lg` and up nothing changes.
 */

export type MobileFabSlot = "chat" | "activity" | "report";

const SLOTS: Record<MobileFabSlot, { label: string; icon: LucideIcon }> = {
  chat: { label: "Chat", icon: MessageCircle },
  activity: { label: "Activity", icon: PanelRight },
  report: { label: "Report a bug / idea", icon: Bug },
};

const SLOT_ORDER: MobileFabSlot[] = ["chat", "activity", "report"];

const BELOW_LG_QUERY = "(max-width: 1023.98px)";

// The bar's height already includes its safe-area padding; with no bar, clear the safe area itself.
const BOTTOM_CLEARANCE = `var(${MOBILE_ACTION_BAR_HEIGHT_VAR}, env(safe-area-inset-bottom, 0px))`;

/** Bottom edge of the FAB: 1rem above the mobile action bar (or the safe area). */
export const MOBILE_FAB_BOTTOM = `calc(${BOTTOM_CLEARANCE} + 1rem)`;

/** Bottom edge for panels opened from the FAB, so they sit just above it. */
export const MOBILE_FAB_PANEL_BOTTOM = `calc(${BOTTOM_CLEARANCE} + 4.5rem)`;

interface MobileFabEntry {
  slot: MobileFabSlot;
  onSelect: () => void;
  badge?: number;
}

interface MobileFabContextValue {
  active: boolean;
  register: (key: symbol, entry: MobileFabEntry) => void;
  unregister: (key: symbol) => void;
}

const MobileFabContext = createContext<MobileFabContextValue | null>(null);

function useBelowLg() {
  const [belowLg, setBelowLg] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(BELOW_LG_QUERY).matches === true,
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(BELOW_LG_QUERY);
    const onChange = () => setBelowLg(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return belowLg;
}

export function MobileFabProvider({
  enabled = true,
  children,
}: {
  enabled?: boolean;
  children: React.ReactNode;
}) {
  const belowLg = useBelowLg();
  // Map keeps first-insertion order, so updating an entry doesn't move it.
  const [entries, setEntries] = useState<Map<symbol, MobileFabEntry>>(() => new Map());

  const register = useCallback((key: symbol, entry: MobileFabEntry) => {
    setEntries((prev) => new Map(prev).set(key, entry));
  }, []);

  const unregister = useCallback((key: symbol) => {
    setEntries((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const active = enabled && belowLg;
  const value = useMemo(() => ({ active, register, unregister }), [active, register, unregister]);

  return (
    <MobileFabContext.Provider value={value}>
      {children}
      {active && <MobileFab entries={entries} />}
    </MobileFabContext.Provider>
  );
}

/**
 * Adds a control's item to the mobile FAB menu. Returns true while the FAB is
 * showing, in which case the caller should not render its own trigger.
 * Outside a MobileFabProvider it does nothing and returns false.
 */
export function useMobileFabItem(
  slot: MobileFabSlot,
  onSelect: () => void,
  { enabled = true, badge }: { enabled?: boolean; badge?: number } = {},
): boolean {
  const ctx = useContext(MobileFabContext);
  const [key] = useState(() => Symbol(slot));
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  });

  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useEffect(() => {
    if (!register || !unregister) return;
    if (enabled) register(key, { slot, badge, onSelect: () => onSelectRef.current() });
    else unregister(key);
  }, [register, unregister, key, slot, badge, enabled]);

  useEffect(() => () => unregister?.(key), [unregister, key]);

  return ctx?.active ?? false;
}

function MobileFab({ entries }: { entries: Map<symbol, MobileFabEntry> }) {
  const itemChosen = useRef(false);

  // If a slot is registered twice (claim detail mounts a second Chat button), the later one wins,
  // matching the old pills where the later one was painted on top.
  const bySlot = new Map<MobileFabSlot, MobileFabEntry>();
  entries.forEach((entry) => bySlot.set(entry.slot, entry));
  const items = SLOT_ORDER.flatMap((slot) => bySlot.get(slot) ?? []);

  if (items.length === 0) return null;

  const unread = bySlot.get("chat")?.badge ?? 0;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={unread > 0 ? `Quick actions, ${unread} unread messages` : "Quick actions"}
          className={cn(
            "fixed right-4 z-40 lg:hidden",
            "flex h-12 w-12 items-center justify-center rounded-full",
            "border border-primary-foreground/10 bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95",
            "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 select-none",
          )}
          style={{ bottom: MOBILE_FAB_BOTTOM }}
          data-testid="mobile-fab"
        >
          <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white ring-2 ring-background"
              data-testid="mobile-fab-unread-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        sideOffset={8}
        className="min-w-[13rem]"
        // The chosen item opens its own panel or dialog; don't pull focus back to the FAB.
        onCloseAutoFocus={(e) => {
          if (itemChosen.current) {
            itemChosen.current = false;
            e.preventDefault();
          }
        }}
        data-testid="mobile-fab-menu"
      >
        {items.map((item) => {
          const { label, icon: Icon } = SLOTS[item.slot];
          return (
            <DropdownMenuItem
              key={item.slot}
              className="gap-3 py-2.5"
              onSelect={() => {
                itemChosen.current = true;
                item.onSelect();
              }}
              data-testid={`mobile-fab-item-${item.slot}`}
            >
              <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span>{label}</span>
              {!!item.badge && (
                <span
                  className={cn(
                    "ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold tabular-nums",
                    item.slot === "chat" ? "bg-red-500 text-white" : "bg-muted text-muted-foreground",
                  )}
                >
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
